<template>
  <div :class="cn(variants({ theme, side, icon: !!icon }), props.class)">
    <div
      v-if="icon"
      id="icon-wrapper"
      class="flex w-12 h-12 p-3 items-center justify-center rounded-full"
    >
      <Icon id="icon" :name="icon" size="2xl" class="shrink-0" />
    </div>
    <div
      class="w-full flex flex-col gap-1 overflow-hidden"
      :class="{
        'items-center justify-center': alignItems === 'center',
      }"
    >
      <Component :is="title ? Primitive : VisuallyHidden" :asChild="!!title">
        <DialogTitle
          id="dialog-title"
          :title="title"
          class="text-style-lg font-semibold text-primary truncate"
          :class="{ 'ltr:mr-12 rtl:ml-12': withClose }"
        >
          {{ title }}
        </DialogTitle>
      </Component>
      <DialogDescription
        v-if="description"
        id="dialog-description"
        class="text-style-sm font-medium text-tertiary"
      >
        {{ description }}
      </DialogDescription>
      <Primitive asChild class="close ltr:right-4 rtl:!right-auto rtl:left-4">
        <slot name="close">
          <DialogClose
            v-if="withClose"
            ref="close"
            :as="CloseButton"
            size="lg"
            :aria-label="$t('components.buttons.close')"
            class="close ltr:right-4 rtl:!right-auto rtl:left-4"
          />
        </slot>
      </Primitive>
      <slot name="tabs">
        <div v-if="withSearch" class="py-3">
          <slot name="search" :query="query.trim().toLowerCase()">
            <DialogSearch v-model="query" />
          </slot>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { Primitive, VisuallyHidden } from "radix-vue";
import { CloseButton } from "@/SharedComponents/Buttons";
import { Icon } from "@/SharedComponents/Icon";
import {
  DialogClose,
  DialogDescription,
  DialogTitle,
  DialogSearch,
  headerVariants as variants,
} from "@/SharedComponents/Dialog";
import { cn } from "@/Utils/cn";

const close = ref(null);

const props = defineProps({
  icon: { type: String, required: false },
  title: { type: String, required: false },
  description: { type: String, required: false },
  theme: { type: String, required: false },
  side: { type: String, required: false },
  withSearch: { type: Boolean, default: false },
  withClose: { type: Boolean, default: true },
  alignItems: {
    type: String,
    validator: (val) => ["start", "center"].includes(val),
    default: "start",
  },
  class: { type: null, required: false },
});

const query = ref("");

function clearQuery() {
  query.value = "";
}

defineExpose({
  query: computed(() => query.value.trim().toLowerCase()),
  clearQuery,
  close: () => close.value?.$el.click(),
});
</script>
